import type {
  MenuListItem,
  MenuListCategoryItem,
  MenuListProductItem,
  ProductInfo,
  PromotionCouponProductsType,
} from '@/apis/product/types';
import { parseObj } from '@/utils';
import { trackEvent } from '@/utils/tracker';
import { daypartMap } from '@/constants';

export const canIUseSession = typeof sessionStorage !== 'undefined';

export const CUR_SELECT_PRODUCT_EXTRA_INFO = 'CUR_SELECT_PRODUCT_EXTRA_INFO';

export type ProductExtraInfo = {
  _code: string;
  _name?: string;
  product_type: number;
  product_rank: number;
  list_name: string;
  primary_list_rank: number;
  second_list_name?: string;
  second_list_rank?: number;
  original_price?: number;
};

export const parseProductExtraInfo = (str?: string): ProductExtraInfo | undefined => parseObj<ProductExtraInfo>(str);

export const getCurProductExtraSessionInfo = () => {
  if (canIUseSession) {
    const _str = sessionStorage.getItem(CUR_SELECT_PRODUCT_EXTRA_INFO);
    return parseObj<ProductExtraInfo>(_str);
  }
  return undefined;
};

export const setCurProductExtraSessionInfo = (info: string) => {
  if (canIUseSession) {
    sessionStorage.setItem(CUR_SELECT_PRODUCT_EXTRA_INFO, info);
  }
};

export const removeCurProductExtraSessionInfo = () => {
  if (canIUseSession) {
    sessionStorage.removeItem(CUR_SELECT_PRODUCT_EXTRA_INFO);
  }
};

export const curProductExtraSyncToSession = (info?: ProductExtraInfo | string) => {
  if (info) {
    const _info = typeof info === 'string' ? info : JSON.stringify(info);
    setCurProductExtraSessionInfo(_info);
  }
};

const queueFunc = <T extends (...args: any) => any>(func: T, ...args: Parameters<T>) => {
  try {
    //  event add to queue
    setTimeout(
      () => {
        func(...args);
      },
      0,
      args,
    );
  } catch (error) {
    // ignore
  }
};

export const gettProductExtraInfoString = (
  orderType = 1,
  product: MenuListProductItem,
  productIdx: number,
  primary: MenuListItem,
  primaryIndex: number,
  second?: MenuListCategoryItem,
  secondIndex?: number,
): string | undefined => {
  if (product && primary) {
    const _info: ProductExtraInfo = {
      _code: product.productCode,
      _name: product.productName,
      product_type: product.productType,
      product_rank: productIdx + 1,
      list_name: primary.categoryName,
      primary_list_rank: primaryIndex + 1,
      second_list_name: second?.categoryName ?? undefined,
      second_list_rank: typeof secondIndex === 'number' ? secondIndex + 1 : undefined,
      original_price: orderType === 2 ? product.priceInfo?.deliveryPrice : product.priceInfo.eatInPrice,
    };

    return JSON.stringify(_info);
  }
  return undefined;
};

/**
 * 浏览商品详情页 / 浏览套餐详情页
 * @param data
 * @param daypartCode
 * @param orderType
 * @param pageSource
 */
export const trackingProductDetailPV = (
  data?: ProductInfo,
  daypartCode?: string | number,
  orderType?: string | number,
  pageSource?: string,
) => {
  const order_time = daypartMap[String(daypartCode) as keyof typeof daypartMap];
  const is_on_sale_product = Boolean(data?.promotionId);

  const _extra = getCurProductExtraSessionInfo();
  const other = _extra?._code === data?.code ? _extra : {};

  const page_source = pageSource || `${Number(orderType) === 2 ? '麦乐送' : '到店取餐'}菜单页`;

  if (data?.comboItems) {
    trackEvent('comboDetail', {
      ...other,
      order_time,
      is_on_sale_product,
      page_source,
      Pitem_code: data.code,
      combo_name: data.name,
    });
  } else if (data) {
    trackEvent('commodityDetail', {
      ...other,
      order_time,
      is_on_sale_product,
      page_source,
      item_code: data.code,
      commodity_name: data.name,
    });
  }
};

const _trackAddToCartEV = (options?: {
  productCode?: string;
  productName?: string;
  comboName?: string;
  presentPrice?: number;
  quantity?: number;
  daypartCode?: string | number;
  orderType?: string | number;
  pageSource?: string;
  isDefault?: boolean;
  couponName?: string;
  comboContent?: string[];
  removeExtraInfo?: boolean;
  other?: Record<string, any>;
}) => {
  if (options?.productCode && options.daypartCode) {
    const _extra = getCurProductExtraSessionInfo();
    const other = !options.removeExtraInfo && _extra?._code === options.productCode ? _extra : {};

    const order_time = daypartMap[String(options.daypartCode) as keyof typeof daypartMap];
    const coupon_type = options.couponName ? (Number(options.orderType) === 2 ? '外送专用' : '到店专用') : undefined;

    if (options?.comboName) {
      trackEvent('addComboToCart', {
        ...other,
        order_time,
        page_source: options.pageSource,
        shoppingcart_entrance: options.pageSource || '套餐详情页',
        is_default_package: options.isDefault ?? true,
        Pitem_code: options.productCode,
        combo_name: options.comboName,
        present_price: options.presentPrice,
        combo_quantity: options.quantity ?? 1,
        coupon_type,
        coupon_name: options.couponName,
        combo_content: options.comboContent,
        ...options.other,
      });
    } else {
      trackEvent('addCommodityToCart', {
        ...other,
        order_time,
        is_default: options.isDefault ?? true,
        shoppingcart_entrance: options.pageSource ?? '菜单页列表区',
        item_code: options.productCode,
        commodity_name: options.productName,
        present_price: options.presentPrice,
        commodity_quantity: options.quantity ?? 1,
        coupon_type,
        coupon_name: options.couponName,
        ...options.other,
      });
    }
  }
};

export const trackAddToCartEV = (...args: Parameters<typeof _trackAddToCartEV>) =>
  queueFunc(_trackAddToCartEV, ...args);

/**
 * 商品加入购物车
 * @param item
 * @param daypartCode
 * @param orderType
 * @param pageSource
 */
export const trackAddToCartByListEV = (
  item?: MenuListProductItem,
  daypartCode?: string | number,
  orderType?: string | number,
  pageSource?: string,
) => {
  if (item) {
    const presentPrice =
      item.priceInfo?.separatePrice ??
      (Number(orderType === 2) ? item.priceInfo?.deliveryPrice : item.priceInfo?.eatInPrice);

    trackAddToCartEV({
      productCode: item.productCode,
      productName: item.productName,
      orderType,
      daypartCode,
      presentPrice,
      pageSource,
      isDefault: true,
      quantity: 1,
    });
  }
};

/**
 *
 * @param product
 * @param couponName
 * @param daypartCode
 * @param orderType
 * @param pageSource
 * @param isDefault
 */
export const trackAddToCartByCouponEV = (
  product?: NonNullable<PromotionCouponProductsType['productList']>[0],
  couponName?: string,
  daypartCode?: string | number,
  orderType?: string | number,
  pageSource?: string,
  isDefault = true,
) => {
  if (product?.productCode) {
    const original_price = Number(orderType === 2) ? product.priceInfo?.deliveryPrice : product.priceInfo?.eatInPrice;
    const presentPrice = product.priceInfo?.discountPrice ?? product.priceInfo?.separatePrice ?? original_price;

    trackAddToCartEV({
      productCode: product.productCode,
      productName: product.productName,
      comboName: product.productType === 1 ? undefined : product.productName,
      orderType,
      daypartCode,
      presentPrice,
      pageSource,
      couponName,
      isDefault,
      removeExtraInfo: true,
      other: {
        original_price,
        product_type: product.productType,
      },
    });
  }
};

/**
 *
 * @param product
 * @param quantity
 * @param daypartCode
 * @param orderType
 * @param isDefault
 * @param presentPrice
 * @param couponName
 * @param pageSource
 */
export const trackAddToCartByDetailEV = (
  product?: ProductInfo,
  quantity?: number,
  daypartCode?: string | number,
  orderType?: string | number,
  isDefault?: boolean,
  presentPrice?: number,
  couponName?: string,
  pageSource?: string,
) => {
  if (product) {
    const isContainCombo = Boolean(product.comboItems);

    trackAddToCartEV({
      productCode: product.code,
      productName: product.name,
      comboName: isContainCombo ? product.name : undefined,
      daypartCode,
      orderType,
      pageSource: pageSource ?? (isContainCombo ? '套餐详情页' : '商品详情页'),
      quantity,
      couponName: couponName,
      presentPrice,
      isDefault,
      other: {
        original_price: product.price,
        product_type: product.type,
      },
    });
  }
};
